import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes, { any } from 'prop-types';
import { range } from 'lodash';

import { CheckBox, Select, Input } from '../../Form';
import { createReactFormValidator, integer, required } from '../../../utils/validation';
import { drawEliminationTypeIds, getPlayersCountListToForwardForPlayoff } from '../../../utils/eventConstants';
import { getMaxPlayersAllowedForPlayoff, needsPlayersAdvancingToPlayoff, shouldDisplayPlayOffDrawSection, shouldDrawSizeBeDisabled } from './utils';
import { getDrawEliminationTypeId, getFeedOutRoundOptions } from '../../../utils/events';

import styles from './eventDrawCreateEditPopup.scss';
import { showInfoPopup } from '../../../actions/notification-actions';

const NonGroupDrawFormatFields = (props) => {
    const {
        values,
        isInEditMode,
        drawToEdit,
        parentDraw,
        proDrawEliminationTypes,
        onDrawEliminationChange,
        allowRoundResize,
        selectableDrawSizes,
        onDrawSizeChange,
        ficFormatTypes,
        onChangeFicFormatTypeId,
        showFicFormatTypeField,
        isDrawSizeChanged,
        feedInBackDrawFormatOptions,
        onChangeFibFeedOutRound,
        isDoubleEliminationDraw,
        playOffDrawOptions,
        onPlayOffDrawCheckChange,
        drawDataFromEditSpecificApi,
        onGroupPlayersAdvancingChange,
        resetGroupPlayersAdvancingToPlayoffCount,
    } = props;

    const [isPlayOffCountChangeConfirmed, setIsPlayOffCountChangeConfirmed] = useState(false);
    const dispatch = useDispatch();

    const playersToAdvanceOptions = useMemo(() => {
        const maxPlayers = getMaxPlayersAllowedForPlayoff(values.eliminationTypeId, values.drawSize, values.matchesPerRound);
        return getPlayersCountListToForwardForPlayoff(2, maxPlayers);
    }, [values.eliminationTypeId, values.drawSize, values.matchesPerRound]);

    const eliminationTypeId = getDrawEliminationTypeId(values);

    const onChangePlayOffDrawCheck = (playOffDrawOption) => (value) => {
        onPlayOffDrawCheckChange(value, playOffDrawOption);
    };

    const onConfirmGroupPlayersAdvancingChange = useCallback((value) => {
        onGroupPlayersAdvancingChange(value);
    }, [onGroupPlayersAdvancingChange]);

    const onChangePlayersAdvancingToPlayoff = useCallback((value) => {
        const isPlayOffCountChangeConfirmationNeeded = isInEditMode &&
            drawDataFromEditSpecificApi?.arePlayersAddedToPlayOff &&
            (parseInt(value) !== parseInt(drawDataFromEditSpecificApi?.groupPlayersAdvancingToPlayoffCount));

        if (isPlayOffCountChangeConfirmationNeeded && !isPlayOffCountChangeConfirmed) {
            dispatch(showInfoPopup({
                title: 'Please Confirm Your Playoff Draw Settings Update',
                message: (
                    <ul className="mb0 mt8">
                        <li className="fs14 mb8">
                            Changing the number of players or teams to advance to the playoffs will clear any players or teams that have already been advanced in this draw&apos;s playoff round.
                        </li>
                        <li className="fs14 mb0">
                            The playoff draw will then be re-sized and the new selection of players/teams will be moved to the playoff draw. All standings will be recalculated.
                        </li>
                    </ul>
                ),
                confirmButtonLabel: 'Confirm',
                cancelButtonLabel: 'Cancel',
                showCancelButton: true,
                showConfirmButton: true,
                noIcon: true,
                onCancelClick: () => {
                    resetGroupPlayersAdvancingToPlayoffCount();
                },
                onConfirmClick: () => {
                    setIsPlayOffCountChangeConfirmed(true);
                    onConfirmGroupPlayersAdvancingChange(value);
                },
            }));
        } else {
            onConfirmGroupPlayersAdvancingChange(value);
        }
        // drawDataFromEditSpecificApi contains the draw data fetched fetched specifically for edit mode. it has information about the options selected while creating the draw and some information related to its parent draw or child draws. Most of this data are not present in a regular draw object.
    }, [isPlayOffCountChangeConfirmed, drawDataFromEditSpecificApi, isInEditMode, onConfirmGroupPlayersAdvancingChange, resetGroupPlayersAdvancingToPlayoffCount]);

    return (
        <>
            {parseInt(values.eliminationTypeId) === drawEliminationTypeIds.PRO_DRAWS && (
                <Select
                    id="eliminationTypeId"
                    label="Parent Draw type"
                    field="parentDrawEliminationTypeId"
                    options={proDrawEliminationTypes}
                    validate={required}
                    onChange={onDrawEliminationChange}
                    disabled={isInEditMode}
                    themeV3
                    data-testid="draw-create.parent-draw-type-select"
                />
            )}
            {allowRoundResize && (
                <div className="row">
                    <div className="col-md-6">
                        <Input
                            id="roundSize"
                            label="Round Size"
                            field="roundSize"
                            type="number"
                            formGroupCssClass="mb0"
                            validate={createReactFormValidator([required, range(1, 52), integer])}
                            themeV3
                            data-testid="draw-create.round-size-input"
                        />
                    </div>
                    <div className="col-md-6">
                        <Input
                            id="matchesPerRound"
                            label="Matches Per Round"
                            field="matchesPerRound"
                            type="number"
                            formGroupCssClass="mb0"
                            validate={createReactFormValidator([required, range(1, 52), integer])}
                            disabled={isInEditMode}
                            themeV3
                            data-testid="draw-create.matches-per-round-input"
                        />
                    </div>
                </div>
            )}
            {!allowRoundResize && (
                <Select
                    id="drawSize"
                    label="Draw size"
                    field="drawSize"
                    options={selectableDrawSizes}
                    validate={required}
                    onChange={onDrawSizeChange}
                    disabled={shouldDrawSizeBeDisabled(isInEditMode, values, drawToEdit, parentDraw)}
                    themeV3
                    data-testid="draw-create.draw-size-select"
                />
            )}
            {isInEditMode && values?.eliminationTypeId === drawEliminationTypeIds.SINGLE_ELIMINATION && (parentDraw?.id === drawToEdit.id) && (
                <Select
                    id="feedOutRound"
                    label="Feed-in-Draw Cutoff Round"
                    field="feedOutRound"
                    options={getFeedOutRoundOptions(parseInt(values.drawSize), parseInt(values.formatTypeId))}
                    themeV3
                    emptyOption="Select last round"
                    hoverText="Subsequent rounds will not be shown on the event profile. Winners of the last round selected in this draw can be manually moved to the first round of the next draw."
                    data-testid="draw-create.feed-out-round-select"
                />
            )}
            {showFicFormatTypeField && (
                <Select
                    id="ficFormatTypeId"
                    label="Feed-in Consolation Format"
                    field="ficFormatTypeId"
                    options={ficFormatTypes}
                    validate={required}
                    onChange={onChangeFicFormatTypeId}
                    disabled={isInEditMode && !isDrawSizeChanged}
                    themeV3
                    data-testid="draw-create.fic-format-select"
                />
            )}
            {parseInt(eliminationTypeId) === drawEliminationTypeIds.FEED_IN_BACK_DRAW && (
                <Select
                    id="feedOutRound"
                    label="Feed-in backdraw format"
                    field="feedOutRound"
                    options={feedInBackDrawFormatOptions}
                    validate={required}
                    onChange={onChangeFibFeedOutRound}
                    disabled={isInEditMode}
                    themeV3
                    data-testid="draw-create.back-draw-format-select"
                />
            )}
            {isDoubleEliminationDraw && (
                <CheckBox
                    id="notAllowMovingWinnersToMainFromBackDraw"
                    formGroupCssClass={`mb10 ${styles.dummy}`}
                    customLabel="Prevent back draw winner(s) from moving to main draw"
                    field="notAllowMovingWinnersToMainFromBackDraw"
                    disabled={isInEditMode}
                    data-testid="draw-create.no-back-draw-winner-checkbox"
                />
            )}
            {shouldDisplayPlayOffDrawSection(isInEditMode, parentDraw, drawToEdit) && playOffDrawOptions?.map((playOffDrawOption) => {
                return (
                    <CheckBox
                        key={`playoff-draw-option-${playOffDrawOption.id}`}
                        id={playOffDrawOption.id}
                        formGroupCssClass={`mb10 ${styles.playOffCheckbox}`}
                        customLabel={`Add ${playOffDrawOption.name}`}
                        field={playOffDrawOption.fieldName}
                        onChange={onChangePlayOffDrawCheck(playOffDrawOption)}
                        disabled={isInEditMode}
                        data-testid={`draw-create.playoff-draw-${playOffDrawOption.id}-checkbox`}
                    />
                );
            })}
            {shouldDisplayPlayOffDrawSection(isInEditMode, parentDraw, drawToEdit) && needsPlayersAdvancingToPlayoff(values.eliminationTypeId) && values.createPlayoffDraw && (
                <>
                    <Select
                        id="groupPlayersAdvancingToPlayoffCount"
                        field="groupPlayersAdvancingToPlayoffCount"
                        label="# of players / teams to advance"
                        hiddenDefault
                        options={playersToAdvanceOptions}
                        attributeToShow="label"
                        attributeToSave="value"
                        themeV3
                        disabled={isInEditMode && !drawDataFromEditSpecificApi?.allowPlayOffDrawUpdate}
                        onChange={onChangePlayersAdvancingToPlayoff}
                        data-testid="draw-create.players-advancing-select"
                    />
                    {values.groupPlayersAdvancingToPlayoffCount >= 4 && parseInt(values.eliminationTypeId) !== drawEliminationTypeIds.AD_HOC && (
                        <CheckBox
                            id="createPlayoffBronzeMatch"
                            formGroupCssClass={`mb10 ${styles.dummy}`}
                            customLabel="Add a bronze medal playoff match"
                            field="createPlayoffBronzeMatch"
                            disabled={isInEditMode && !drawDataFromEditSpecificApi?.allowPlayOffDrawUpdate}
                            data-testid="draw-create.bronze-match-checkbox"
                        />
                    )}
                </>
            )}
        </>
    );
};

NonGroupDrawFormatFields.propTypes = {
    values: PropTypes.objectOf(any).isRequired,
    isInEditMode: PropTypes.bool,
    drawToEdit: PropTypes.objectOf(any),
    parentDraw: PropTypes.objectOf(any),
    proDrawEliminationTypes: PropTypes.arrayOf(any),
    onDrawEliminationChange: PropTypes.func.isRequired,
    allowRoundResize: PropTypes.bool,
    selectableDrawSizes: PropTypes.arrayOf(any).isRequired,
    onDrawSizeChange: PropTypes.func.isRequired,
    ficFormatTypes: PropTypes.arrayOf(any).isRequired,
    onChangeFicFormatTypeId: PropTypes.func.isRequired,
    showFicFormatTypeField: PropTypes.bool,
    isDrawSizeChanged: PropTypes.bool,
    feedInBackDrawFormatOptions: PropTypes.arrayOf(any).isRequired,
    onChangeFibFeedOutRound: PropTypes.func.isRequired,
    isDoubleEliminationDraw: PropTypes.bool,
    playOffDrawOptions: PropTypes.arrayOf(any).isRequired,
    onPlayOffDrawCheckChange: PropTypes.func.isRequired,
    drawDataFromEditSpecificApi: PropTypes.objectOf(any),
    onGroupPlayersAdvancingChange: PropTypes.func,
    resetGroupPlayersAdvancingToPlayoffCount: PropTypes.func,
};

NonGroupDrawFormatFields.defaultProps = {
    isInEditMode: false,
    drawToEdit: null,
    parentDraw: null,
    proDrawEliminationTypes: [],
    allowRoundResize: false,
    showFicFormatTypeField: false,
    isDrawSizeChanged: false,
    isDoubleEliminationDraw: false,
    drawDataFromEditSpecificApi: null,
    onGroupPlayersAdvancingChange: () => {},
    resetGroupPlayersAdvancingToPlayoffCount: () => {},
};

export default NonGroupDrawFormatFields;
